import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtMoney, fmtDay } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { elementToPdf } from "@/lib/pdf";
import { toast } from "sonner";
import { ArrowLeft, Download, Printer } from "lucide-react";

const ST_COLORS = { "Émise": "text-slate-600", "Partiellement payée": "text-amber-600", "Payée": "text-emerald-600" };

export default function ProStatement() {
  const { id } = useParams();
  const nav = useNavigate();
  const { settings } = useAuth();
  const cur = settings?.currency || "KMF";
  const docRef = useRef(null);
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/pro/clients/${id}/statement`).then((r) => setD(r.data)).catch((e) => toast.error(apiError(e)));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!d) return <div className="text-slate-400 p-8">Chargement...</div>;
  const { client, invoices, payments, settings: s, total_billed, total_paid, total_balance } = d;
  const company = s?.company_name || "WuraPro";
  const fileName = `Releve_${company}_${(client?.name || "").replace(/[^a-zA-Z0-9]/g, "")}.pdf`.replace(/\s/g, "");
  const download = async () => { setBusy(true); try { await elementToPdf(docRef.current, fileName, { width: 800 }); } catch (e) { toast.error("Erreur PDF"); } finally { setBusy(false); } };

  return (
    <div className="space-y-4" data-testid="pro-statement-page">
      <div className="no-print flex flex-wrap items-center gap-2">
        <button onClick={() => nav(`/pro/clients/${id}`)} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mr-auto"><ArrowLeft size={16} /> Retour à la fiche</button>
        <Button variant="outline" data-testid="stmt-download" onClick={download} disabled={busy}><Download size={16} className="mr-1.5" /> Télécharger</Button>
        <Button variant="outline" data-testid="stmt-print" onClick={() => window.print()}><Printer size={16} className="mr-1.5" /> Imprimer</Button>
      </div>

      <div ref={docRef} className="print-area bg-white mx-auto max-w-3xl p-10 rounded-xl border border-slate-200 shadow-sm text-slate-900" data-testid="statement-document" style={{ fontFamily: "Arial, Helvetica, sans-serif" }}>
        <div className="flex items-start gap-3 border-b-2 border-slate-800 pb-4">
          {s?.logo_url && <img src={s.logo_url} alt="logo" className="h-16 w-16 rounded object-cover" />}
          <div>
            <p className="text-2xl font-extrabold">{company}</p>
            <p className="text-sm text-slate-600">Blanchisserie Professionnelle</p>
            {s?.address && <p className="text-xs text-slate-500 mt-0.5">{s.address}</p>}
            {s?.whatsapp && <p className="text-xs text-slate-500">Tél. : {s.whatsapp}</p>}
          </div>
        </div>

        <p className="text-center text-xl font-extrabold mt-6 tracking-wide">RELEVÉ DE COMPTE</p>
        <div className="mt-4 text-sm">
          <p className="font-semibold underline">Client</p>
          <p className="font-bold">{client?.name}</p>
          {client?.phone && <p className="text-slate-600">Tél. : {client.phone}</p>}
          <p className="text-xs text-slate-500 mt-1">Édité le {fmtDay(new Date().toISOString())}</p>
        </div>

        <p className="mt-5 text-sm font-semibold">Factures</p>
        <table className="w-full text-sm mt-2 border-collapse">
          <thead><tr className="border-2 border-slate-800 bg-slate-100">
            <th className="px-2 py-2 border border-slate-400 text-left">N° facture</th>
            <th className="px-2 py-2 border border-slate-400 text-left">Période</th>
            <th className="px-2 py-2 border border-slate-400 text-right">Total</th>
            <th className="px-2 py-2 border border-slate-400 text-right">Payé</th>
            <th className="px-2 py-2 border border-slate-400 text-right">Solde</th>
            <th className="px-2 py-2 border border-slate-400 text-left">Statut</th>
          </tr></thead>
          <tbody>
            {invoices.map((i) => (
              <tr key={i.id}>
                <td className="px-2 py-1.5 border border-slate-300">{i.number}</td>
                <td className="px-2 py-1.5 border border-slate-300">{fmtDay(i.period_from)} → {fmtDay(i.period_to)}</td>
                <td className="px-2 py-1.5 border border-slate-300 text-right">{fmtMoney(i.total_amount, cur)}</td>
                <td className="px-2 py-1.5 border border-slate-300 text-right">{fmtMoney(i.amount_paid, cur)}</td>
                <td className="px-2 py-1.5 border border-slate-300 text-right">{fmtMoney(i.balance, cur)}</td>
                <td className={`px-2 py-1.5 border border-slate-300 ${ST_COLORS[i.status] || ""}`}>{i.status}</td>
              </tr>
            ))}
            {invoices.length === 0 && <tr><td colSpan={6} className="px-2 py-3 border border-slate-300 text-center text-slate-400">Aucune facture</td></tr>}
            <tr className="font-bold bg-slate-100 border-2 border-slate-800">
              <td className="px-2 py-2 border border-slate-400" colSpan={2}>TOTAUX</td>
              <td className="px-2 py-2 border border-slate-400 text-right">{fmtMoney(total_billed, cur)}</td>
              <td className="px-2 py-2 border border-slate-400 text-right">{fmtMoney(total_paid, cur)}</td>
              <td className="px-2 py-2 border border-slate-400 text-right">{fmtMoney(total_balance, cur)}</td>
              <td className="px-2 py-2 border border-slate-400"></td>
            </tr>
          </tbody>
        </table>

        <p className="mt-6 text-sm font-semibold">Paiements reçus</p>
        <table className="w-full text-sm mt-2 border-collapse">
          <thead><tr className="border-2 border-slate-800 bg-slate-100">
            <th className="px-2 py-2 border border-slate-400 text-left">Date</th>
            <th className="px-2 py-2 border border-slate-400 text-left">Mode</th>
            <th className="px-2 py-2 border border-slate-400 text-left">Référence</th>
            <th className="px-2 py-2 border border-slate-400 text-right">Montant</th>
          </tr></thead>
          <tbody>
            {payments.map((p) => (
              <tr key={p.id}>
                <td className="px-2 py-1.5 border border-slate-300">{fmtDay(p.date)}</td>
                <td className="px-2 py-1.5 border border-slate-300">{p.method}</td>
                <td className="px-2 py-1.5 border border-slate-300">{p.reference || "—"}</td>
                <td className="px-2 py-1.5 border border-slate-300 text-right">{fmtMoney(p.amount, cur)}</td>
              </tr>
            ))}
            {payments.length === 0 && <tr><td colSpan={4} className="px-2 py-3 border border-slate-300 text-center text-slate-400">Aucun paiement</td></tr>}
          </tbody>
        </table>

        <div className="mt-6 flex justify-end">
          <div className="text-sm border-2 border-slate-800 px-4 py-2">
            <span className="text-slate-600 mr-4">Solde restant dû</span>
            <span className="font-extrabold text-base">{fmtMoney(total_balance, cur)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
